import {useState} from 'react'
import classnames from 'classnames'
import {connect} from 'react-redux'
import {List, Map} from 'immutable'
import PropTypes from 'prop-types'
import {updateResource, readEndpoint} from 'redux-json-api'

import * as ButtonTypes from '../../../../../shared_components/ButtonTypes'

import {notifyAfterSend, sendClientInvitation} from '../../../../../lib/clientInviteTools'
import {isPlaceholderEmail} from '../../../../../lib/emailTools'
import {goToInviteClientModal, goToInviteQuarterbackClientModal} from '../../../../../lib/navigation/proNavigation'
import {currentUserConfig} from '../../../../../lib/userTools'
import {findRawResourceById} from '../../../../../lib/plan_data/userData'
import {useNotificationContext} from '../../../../../shared_components/notifications/NotificationContext'

import './inviteClientLink.scss'

export const InviteClientLink = props => {
  const [processing, setProcessing] = useState(false)
  const {alwaysNotify} = useNotificationContext()

  const sendInvitation = () => {
    const existingClientSeat = props.clientSeats.filterNot(seat => seat.get('id') === props.clientSeat.get('id')).first()
    const existingClient = props.clients.find(client => client.client_seat_id === (existingClientSeat && parseInt(existingClientSeat.get('id'), 10)))


    // In case users have a tad bit slow internet connection, this ensures that in addition to the visual cues showing that the resend invitation link should not be clicked after trying to resend
    // The link does nothing if there is a pending request
    if (!processing) {
      setProcessing(true)

      sendClientInvitation({...props, isQuarterback: props.userConfig.get('quarterback')}).then(() => {
        setProcessing(false)
        props.readEndpoint(`client-stats/${props.userConfig.get('firm-id')}`)
        notifyAfterSend({alwaysNotify, client: props.client, existingClient})
      })
    }
  }

  const handleClick = () => {
    if (props.organization.get('organization-client-invite') ===
      'engagement_automation') {
      sendInvitation()
    } else if (props.userConfig.get('quarterback')) {
      goToInviteQuarterbackClientModal({
        clientSeatId: props.clientSeatIdInParam,
        inviteSeatId: props.clientSeat.get('id')
      })
    } else {
      goToInviteClientModal({
        clientSeatId: props.clientSeatIdInParam,
        inviteSeatId: props.clientSeat.get('id')
      })
    }
  }

  const linkText = `${processing ? 'Resending' : 'Resend'} invitation`

  const disabledExplanation = () => {
    // Sales Studio shell firms hold client invites until an account manager marks
    // setup complete - the backend rejects the send, so don't offer the button.
    if (props.firm.get('pending-setup'))
      return "An invitation cannot be sent while this firm's setup is incomplete. It will be sent automatically once setup is marked complete."

    // Clients created from Sales Studio without a usable email get a placeholder email
    // that must never be invited. The email has to be updated to a real one first.
    if (isPlaceholderEmail(props.client && props.client.email))
      return "An invitation cannot be sent until the client's email address is updated."
  }

  const explanation = disabledExplanation()

  if (explanation) {
    return (
      <div className='invite-client-link-disabled'>
        <ButtonTypes.Continue
          className='send-invitation-button'
          disabled={true}>
          Send invitation
        </ButtonTypes.Continue>
        <p className='disabled-explanation' role='note'>{explanation}</p>
      </div>
    )
  }

  if (props.clientSeat.get('status') === 'Pending') {
    return (
      <a
        className={classnames('resend-invite-link', {processing})}
        onClick={handleClick}>
        {linkText}
      </a>
    )
  } else {
    return (
      <ButtonTypes.Continue
        className='send-invitation-button'
        data-testid='invite-client-link'
        loading={processing}
        onClick={handleClick}>
        Send invitation
      </ButtonTypes.Continue>
    )
  }
}

InviteClientLink.defaultProps = {firm: Map()}

InviteClientLink.propTypes = {
  client: PropTypes.shape({
    name: PropTypes.string,
    first_name: PropTypes.string,
    email: PropTypes.string,
    status: PropTypes.string
  }),
  clients: PropTypes.arrayOf(PropTypes.object),
  clientSeatIdInParam: PropTypes.string,
  clientSeat: PropTypes.instanceOf(Map),
  firm: PropTypes.instanceOf(Map),
  userConfig: PropTypes.instanceOf(Map),
  clientSeats: PropTypes.instanceOf(List),
  readEndpoint: PropTypes.func,
  updateResource: PropTypes.func,
  organization: PropTypes.instanceOf(Map)
}

const mapStateToProps = ({api}, ownProps) => ({
  userConfig: currentUserConfig(api),
  firm: findRawResourceById({rawResource: api.firms, id: ownProps.clientSeat.get('firm-id')})
})

export default connect(mapStateToProps, {updateResource, readEndpoint})(InviteClientLink)
